/**
 * Tax returns, laid out like Property Portfolio > Bookings (Fan, 2026-10-28):
 * filter bar, one row per month that expands into the returns filed (or due)
 * that month, each with its dates, a Paid / Not paid badge and the PDF.
 * Read-only: rows come from GET /payroll/vat-returns, files stream from the
 * accountant's original email via /payroll/vat-returns/{id}/file/{i}.
 * One currency (Fan, 2026-10-30): every figure is in the currency toggle's
 * currency, one number per cell; a Currency column says which currency the
 * return itself is in, and the native amount sits in the cell's tooltip.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Filter, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { payrollAPI } from '../../lib/api';
import {
  RETURN_STATUS, returnStatus, filterOptions, applyFilters, buildReturnGroups, DISPLAY_KEY,
} from '../../lib/taxReturns';

const TONE = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  grey: 'bg-muted text-muted-foreground',
};

function fmtDate(d) {
  if (!d) return null;
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return dd ? `${dd}/${m}/${y}` : d;
}

async function openReturnFile(id, index) {
  try {
    const res = await payrollAPI.getVatReturnFile(id, index);
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error('Could not open the return');
  }
}

function MultiSelect({ label, options, selected, setSelected, render = (v) => v, testId }) {
  return (
    <select
      className="text-sm border rounded px-2 py-1 bg-card"
      value=""
      data-testid={testId}
      onChange={(e) => {
        const v = e.target.value;
        if (!v) return;
        setSelected((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
      }}
    >
      <option value="">{label} ({selected.length || 'All'})</option>
      {options.map((o) => (
        <option key={o} value={o}>{selected.includes(o) ? '\u2713 ' : ''}{render(o)}</option>
      ))}
    </select>
  );
}

export function TaxReturnsTable({ returns, year, nativeFmt, regionLabel, conv = null, dc = 'GBP' }) {
  const rows = useMemo(() => returns || [], [returns]);
  const [companies, setCompanies] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [expanded, setExpanded] = useState(new Set());

  const opts = useMemo(() => filterOptions(rows), [rows]);
  const filtered = useMemo(() => applyFilters(rows, { companies, taxes, statuses }), [rows, companies, taxes, statuses]);
  const { groups, totals } = useMemo(() => buildReturnGroups(filtered, year, new Date(), conv), [filtered, year, conv]);

  // Sums: with conv there is one figure per cell, in the display currency.
  const multi = (byCcy, cls) => {
    const parts = Object.entries(byCcy || {}).filter(([, v]) => Math.abs(v) > 0.005);
    if (!parts.length) return '\u2014';
    return <span className={cls}>{parts.map(([c, v]) => nativeFmt(v, c === DISPLAY_KEY ? dc : c)).join(' + ')}</span>;
  };
  // One amount in the display currency; native amount in the tooltip.
  const one = (v, ccy) => {
    const c = String(ccy || 'GBP').toUpperCase();
    if (!conv || c === dc) return nativeFmt(v, c);
    const d = conv(v, c);
    if (d === null || d === undefined || !Number.isFinite(d)) return <span title="No exchange rate">{nativeFmt(v, c)}</span>;
    return <span title={nativeFmt(v, c)}>{nativeFmt(d, dc)}</span>;
  };
  const anyFilter = companies.length + taxes.length + statuses.length > 0;
  const now = new Date();
  const isCurrentYear = Number(year) === now.getFullYear();

  return (
    <div className="space-y-4" data-testid="tax-vat-returns">
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" /> Filters:
            </div>
            <MultiSelect label="Company" options={opts.companies} selected={companies} setSelected={setCompanies} testId="returns-filter-company" />
            <MultiSelect label="Tax" options={opts.taxes} selected={taxes} setSelected={setTaxes} testId="returns-filter-tax" />
            <MultiSelect label="Status" options={opts.statuses} selected={statuses} setSelected={setStatuses} render={(k) => RETURN_STATUS[k].label} testId="returns-filter-status" />
            {anyFilter && (
              <>
                {companies.map((c) => <Badge key={c} variant="secondary" className="text-xs cursor-pointer" onClick={() => setCompanies((p) => p.filter((x) => x !== c))}>{c} x</Badge>)}
                {taxes.map((c) => <Badge key={c} variant="secondary" className="text-xs cursor-pointer" onClick={() => setTaxes((p) => p.filter((x) => x !== c))}>{c} x</Badge>)}
                {statuses.map((c) => <Badge key={c} variant="secondary" className="text-xs cursor-pointer" onClick={() => setStatuses((p) => p.filter((x) => x !== c))}>{RETURN_STATUS[c].label} x</Badge>)}
                <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => { setCompanies([]); setTaxes([]); setStatuses([]); }}>
                  Clear all
                </Button>
              </>
            )}
            <span className="text-xs text-muted-foreground ml-auto">{totals.count} items</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">No {regionLabel} tax returns recorded for {year} yet.</p>
          ) : (
            <div className="overflow-x-auto">
              {/* Fixed column widths: opening a month must not move the
                  columns (Fan, 2026-10-28: "make sure it doesn't jump"). */}
              <Table className="table-fixed min-w-[840px]">
                <colgroup>
                  <col style={{ width: '2rem' }} />
                  <col />
                  <col style={{ width: '5.5rem' }} />
                  <col style={{ width: '9.5rem' }} />
                  <col style={{ width: '9.5rem' }} />
                  <col style={{ width: '9.5rem' }} />
                  <col style={{ width: '9.5rem' }} />
                </colgroup>
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">To pay</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Not paid</TableHead>
                    <TableHead className="text-right">Refund</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => {
                    const has = g.returns.length > 0;
                    const open = expanded.has(g.month);
                    const isCurrent = isCurrentYear && g.month === now.getMonth() + 1;
                    const isUndated = g.month === 0;
                    const toggle = () => setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(g.month)) next.delete(g.month); else next.add(g.month);
                      return next;
                    });
                    return (
                      <React.Fragment key={g.month}>
                        <TableRow
                          className={cn(isCurrent && 'bg-primary/5', has && 'cursor-pointer hover:bg-muted/50')}
                          onClick={has ? toggle : undefined}
                          data-testid={`returns-month-${g.month}`}
                        >
                          <TableCell className="px-2">
                            {has && (open
                              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                              : <ChevronDown className="h-4 w-4 text-muted-foreground" />)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {g.label}
                            {isCurrent && <Badge className="ml-2 text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0">Now</Badge>}
                            {has && <span className="ml-2 text-xs text-muted-foreground">{g.returns.length} item{g.returns.length !== 1 ? 's' : ''}</span>}
                            {isUndated && <span className="ml-2 text-xs text-amber-700">owed, no due date yet</span>}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right font-mono">{has ? multi(g.toPay) : '\u2014'}</TableCell>
                          <TableCell className="text-right font-mono">{has ? multi(g.paid, 'text-green-700') : '\u2014'}</TableCell>
                          <TableCell className="text-right font-mono">{has ? multi(g.notPaid, 'text-red-600') : '\u2014'}</TableCell>
                          <TableCell className="text-right font-mono">{has ? multi(g.refund, 'text-emerald-700') : '\u2014'}</TableCell>
                        </TableRow>
                        {open && g.returns.map((r) => {
                          const st = returnStatus(r);
                          const amt = Number(r.amount) || 0;
                          return (
                            <TableRow key={r.id} className="bg-muted/20 text-xs" data-testid={`return-row-${r.id}`}>
                              <TableCell></TableCell>
                              <TableCell className="py-1.5 whitespace-normal break-words">
                                <span className="font-medium">{r.entity_name || r.entity_id}</span>
                                <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">{r.tax || 'VAT'}{r.form ? ` ${r.form}` : ''}</Badge>
                                <span className="text-muted-foreground ml-2">{r.period_label}</span>
                                <span className="text-muted-foreground ml-2">
                                  {r.item_type && r.item_type !== 'return'
                                    ? r.date_label
                                    : <>
                                      {r.filed_date ? `Filed ${fmtDate(r.filed_date)}` : 'Not filed'}
                                      {r.due_date ? `, due ${fmtDate(r.due_date)}` : ''}
                                    </>}
                                </span>
                                <Badge className={cn('ml-2 text-[10px] px-1 py-0 border-0', TONE[RETURN_STATUS[st].tone])}>{RETURN_STATUS[st].label}</Badge>
                                {(r.attachments || []).map((a) => (
                                  <button
                                    key={a.index}
                                    type="button"
                                    className="ml-2 inline-flex items-center gap-0.5 text-primary hover:underline"
                                    onClick={(e) => { e.stopPropagation(); openReturnFile(r.id, a.index); }}
                                    title={a.filename}
                                    data-testid={`return-file-${r.id}-${a.index}`}
                                  >
                                    <Paperclip className="h-3 w-3" />PDF
                                  </button>
                                ))}
                                {r.note && <p className="text-muted-foreground mt-0.5">{r.note}</p>}
                                {(r.bank_lines || []).length > 0 && (
                                  <p className="text-muted-foreground mt-0.5" title={r.bank_lines.map((b) => nativeFmt(Math.abs(b.amount), b.currency)).join(', ')}>
                                    Bank: {r.bank_lines.map((b) => `${b.amount < 0 ? 'paid' : 'received'} on ${fmtDate(b.date)}`).join(', ')}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell className="py-1.5" data-testid={`return-ccy-${r.id}`}>{String(r.currency || 'GBP').toUpperCase()}</TableCell>
                              <TableCell className="text-right font-mono py-1.5">{amt > 0 ? one(amt, r.currency) : '\u2014'}</TableCell>
                              <TableCell className="text-right font-mono py-1.5 text-green-700">{amt > 0 && st === 'paid' ? one(amt, r.currency) : '\u2014'}</TableCell>
                              <TableCell className="text-right font-mono py-1.5 text-red-600">{amt > 0 && st !== 'paid' && st !== 'nil' ? one(amt, r.currency) : '\u2014'}</TableCell>
                              <TableCell className="text-right font-mono py-1.5 text-emerald-700">{amt < 0 ? one(-amt, r.currency) : '\u2014'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <TableRow className="border-t-2 font-semibold bg-muted/30">
                    <TableCell></TableCell>
                    <TableCell>Total ({totals.count} items)</TableCell>
                    <TableCell>{conv ? dc : ''}</TableCell>
                    <TableCell className="text-right font-mono">{multi(totals.toPay)}</TableCell>
                    <TableCell className="text-right font-mono">{multi(totals.paid, 'text-green-700')}</TableCell>
                    <TableCell className="text-right font-mono">{multi(totals.notPaid, 'text-red-600')}</TableCell>
                    <TableCell className="text-right font-mono">{multi(totals.refund, 'text-emerald-700')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        {conv && <>All amounts in {dc}, converted at the report's rates; the Currency column is the return's own currency (hover an amount for it).{totals.unconverted ? ` ${totals.unconverted} item${totals.unconverted !== 1 ? 's' : ''} left out of the totals: no exchange rate.` : ''} </>}
        Returns, payment plans, PAYE months, notices and every bank payment or refund to a tax office. A return sits in the month it was filed (or is due, if not filed yet); a debt in the month it is due; a bank line in the month it left or reached the bank. Refunds are often set off by the tax office against tax still owed, so they do not reach the bank.
      </p>
    </div>
  );
}

export default TaxReturnsTable;
